import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { AdminAuthStateService } from './auth.service';
import { authLoginPath } from '../guards/auth.guard';

/** Login/MFA step endpoints — 401 here means bad OTP or stale login, not an expired app session. */
export function isPublicAuthApiPath(url: string): boolean {
  const p = url.split('?')[0];
  return (
    p.includes('/admin/auth/login') ||
    p.includes('/admin/auth/forgot-password') ||
    p.includes('/admin/auth/verify-mfa') ||
    p.includes('/admin/auth/logout') ||
    // legacy tenant-gateway paths (kept for backward compat)
    p.includes('/auth/admin/login') ||
    p.includes('/auth/admin/create-password') ||
    p.includes('/auth/admin/forgot-password') ||
    p.includes('/auth/forgot-password') ||
    p.includes('/auth/password-token') ||
    p.includes('/auth/admin/verify-mfa') ||
    p.includes('/auth/create-password') ||
    p.includes('/auth/admin/logout')
  );
}

/** Detect auth/session failure from API envelope bodies (HTTP 200 + statusCode). */
export function isAuthFailureEnvelope(body: unknown): boolean {
  if (!body || typeof body !== 'object') return false;
  const b = body as { status?: boolean; statusCode?: number; message?: unknown };
  const code = typeof b.statusCode === 'number' ? b.statusCode : null;
  if (code === 401) return true;
  const msg = typeof b.message === 'string' ? b.message.toLowerCase() : '';
  if (code === 403 && /unauthorized|expired|invalid token|jwt|session/.test(msg)) return true;
  return b.status === false && code === 401;
}

export function isAuthFailureHttpError(error: HttpErrorResponse, requestUrl?: string): boolean {
  if (requestUrl && isPublicAuthApiPath(requestUrl)) return false;
  if (error.status === 401) return true;
  if (error.status === 403 && isAuthFailureEnvelope(error.error)) return true;
  if (isAuthFailureEnvelope(error.error)) return true;
  return false;
}

export function shouldRedirectOnAuthFailure(requestUrl: string, bodyOrError?: unknown): boolean {
  if (isPublicAuthApiPath(requestUrl)) return false;
  if (bodyOrError instanceof HttpErrorResponse) {
    return isAuthFailureHttpError(bodyOrError, requestUrl);
  }
  return isAuthFailureEnvelope(bodyOrError);
}

/**
 * Clears local session and navigates to sign-in once (avoids duplicate redirects from parallel 401s).
 */
@Injectable({ providedIn: 'root' })
export class AuthSessionRedirectService {
  private readonly authState = inject(AdminAuthStateService);
  private readonly router = inject(Router);
  private redirecting = false;

  redirectToLogin(returnUrl?: string): void {
    if (globalThis.window === undefined) return;
    const path = this.router.url.split('?')[0];
    if (path.startsWith('/auth')) return;
    if (this.redirecting) return;

    this.redirecting = true;
    this.authState.logout();

    const url = returnUrl ?? this.router.url;
    const queryParams =
      url && url !== '/' && !url.startsWith('/auth') ? { returnUrl: url } : undefined;

    this.router
      .navigate(['/auth', authLoginPath], { replaceUrl: true, queryParams })
      .finally(() => {
        globalThis.setTimeout(() => {
          this.redirecting = false;
        }, 400);
      });
  }
}
